import { useState, useEffect } from 'react';
import { auth } from './firebase';
import { subscribeToUserProfile } from './userProfile';
import { UserProfile } from './types';
import { createBillingPortalUrl, createExtraCallsCheckoutUrl } from './billingApi';
import { currentMarket } from './market';
import './App.css';

function Plans() {
  const currentUser = auth.currentUser;
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  // Which portal button is opening, if any
  const [portalLoading, setPortalLoading] = useState<'' | 'manage' | 'change_plan'>('');
  const [portalError, setPortalError] = useState('');
  const [extraLoading, setExtraLoading] = useState(false);
  const [extraError, setExtraError] = useState('');
  // Checkout returns here after a purchase: ?extra_calls=purchased from "Buy
  // extra calls", ?checkout=complete from the pricing table. The webhook adds
  // the credits a moment later and the live profile shows them.
  const returnParams = new URLSearchParams(window.location.search);
  const purchased = returnParams.get('extra_calls') === 'purchased' || returnParams.get('checkout') === 'complete';
  const market = currentMarket();

  const openBillingPortal = async (flow?: 'change_plan') => {
    setPortalError('');
    setPortalLoading(flow || 'manage');
    try {
      window.location.href = await createBillingPortalUrl(flow);
    } catch (err) {
      setPortalError(err instanceof Error ? err.message : 'Could not open billing.');
      setPortalLoading('');
    }
  };

  const portalButtonStyle = {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    background: 'transparent',
    color: 'inherit',
    fontSize: '14px',
    fontWeight: 500,
    cursor: portalLoading ? 'wait' : 'pointer',
  };

  const buyExtraCalls = async () => {
    setExtraError('');
    setExtraLoading(true);
    try {
      window.location.href = await createExtraCallsCheckoutUrl(market.id);
    } catch (err) {
      setExtraError(err instanceof Error ? err.message : 'Could not open checkout.');
      setExtraLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    return subscribeToUserProfile(currentUser.uid, (profile) => {
      setUserProfile(profile);
      setLoading(false);
    });
  }, [currentUser]);

  const formatPlanName = (plan: string, billingInterval?: string): string => {
    if (plan === 'free') return 'One-off';
    if (plan === 'callout_10') {
      if (!billingInterval) return 'Callout 10';
      return billingInterval === 'year' ? 'Callout 10 - Annual' : 'Callout 10 - Monthly';
    }
    if (plan === 'callout_20') {
      if (!billingInterval) return 'Callout 20';
      return billingInterval === 'year' ? 'Callout 20 - Annual' : 'Callout 20 - Monthly';
    }
    if (plan === 'callout_50') {
      if (!billingInterval) return 'Callout 50';
      return billingInterval === 'year' ? 'Callout 50 - Annual' : 'Callout 50 - Monthly';
    }
    return plan;
  };

  const calculateNextRefillDate = (profile: UserProfile): string => {
    if (profile.subscriptionPlan === 'free') return 'N/A';

    // Check if we have the necessary data
    if (!profile.billingInterval || !profile.subscriptionStartDate) {
      return 'Unknown';
    }

    if (profile.billingInterval === 'month' && profile.subscriptionStartDate) {
      const startDate = profile.subscriptionStartDate.toDate();
      const nextRefill = new Date(startDate);
      nextRefill.setMonth(nextRefill.getMonth() + 1);
      
      // If next refill is in the past, keep adding months until we get a future date
      const now = new Date();
      while (nextRefill < now) {
        nextRefill.setMonth(nextRefill.getMonth() + 1);
      }
      
      return nextRefill.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    if (profile.billingInterval === 'year' && profile.lastMonthlyCredit) {
      const lastCredit = profile.lastMonthlyCredit.toDate();
      const nextRefill = new Date(lastCredit);
      nextRefill.setDate(nextRefill.getDate() + 30);
      return nextRefill.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    return 'Unknown';
  };

  return (
    <div className="App">
      <div className="App-header" style={{ justifyContent: 'flex-start', paddingTop: '80px' }}>
        {/* <h2 style={{ marginBottom: '24px' }}>Plans</h2> */}

        {/* Current Plan Information */}
        {!loading && userProfile && (
          <div style={{
            width: 'calc(100% - 48px)',
            maxWidth: '1100px',
            padding: '24px',
            margin: '0 auto 32px auto',
            backgroundColor: 'var(--bg-card)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Current Subscription</h3>
              {(userProfile.stripeCustomerId || userProfile.subscriptionPlan !== 'free') && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {userProfile.subscriptionPlan !== 'free' && (
                    <button
                      type="button"
                      onClick={() => openBillingPortal('change_plan')}
                      disabled={!!portalLoading}
                      style={portalButtonStyle}
                    >
                      {portalLoading === 'change_plan' ? 'Opening…' : 'Change plan'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openBillingPortal()}
                    disabled={!!portalLoading}
                    style={portalButtonStyle}
                  >
                    {portalLoading === 'manage' ? 'Opening…' : 'Manage subscription'}
                  </button>
                </div>
              )}
            </div>
            {portalError && (
              <div style={{ color: '#e5484d', fontSize: '14px', margin: '-8px 0 16px' }}>{portalError}</div>
            )}
            <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '4px' }}>Plan</div>
                <div style={{ fontSize: '16px', fontWeight: '500' }}>
                  {formatPlanName(userProfile.subscriptionPlan, userProfile.billingInterval)}
                </div>
              </div>
              <div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '4px' }}>Remaining Credits</div>
                <div style={{ fontSize: '16px', fontWeight: '500' }}>
                  {userProfile.creditsRemaining}
                </div>
              </div>
              <div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '4px' }}>Next Refill</div>
                <div style={{ fontSize: '16px', fontWeight: '500' }}>
                  {calculateNextRefillDate(userProfile)}
                </div>
              </div>
              {userProfile.cancelAt && userProfile.subscriptionPlan !== 'free' && (
                <div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '4px' }}>Cancels On</div>
                  <div style={{ fontSize: '16px', fontWeight: '500' }}>
                    {userProfile.cancelAt.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                </div>
              )}
            </div>
            {userProfile.subscriptionPlan !== 'free' && (
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginTop: '16px' }}>
                Unused calls roll over for one month. At each refill you keep up to one month's worth; the rest expires.
                {' '}Upgrades start right away and add the new plan's extra calls; downgrades start at your next renewal.
              </div>
            )}
            {userProfile.subscriptionPlan !== 'free' && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap',
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid var(--border-color)',
              }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '500' }}>Need more calls?</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '4px' }}>
                    Extra calls are {market.extraCallPrice.subscriber} each on your plan, instead of {market.extraCallPrice.standard}.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={buyExtraCalls}
                  disabled={extraLoading}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'transparent',
                    color: 'inherit',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: extraLoading ? 'wait' : 'pointer',
                  }}
                >
                  {extraLoading ? 'Opening…' : 'Buy extra calls'}
                </button>
              </div>
            )}
            {extraError && (
              <div style={{ color: '#e5484d', fontSize: '14px', marginTop: '8px' }}>{extraError}</div>
            )}
            {purchased && (
              <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '8px' }}>
                Thanks for your purchase! Your calls show up in Remaining Credits within a minute.
              </div>
            )}
          </div>
        )}

        <div style={{ width: '100%', maxWidth: '1100px', padding: '0 24px', boxSizing: 'border-box' }}>
          {/* @ts-expect-error stripe-pricing-table is a web component loaded via external script */}
          <stripe-pricing-table
            pricing-table-id={market.pricingTableId}
            publishable-key="pk_live_51NgJYBHjnBdpOfn3O5Wj5Mh94sl5o5KgPzBhVDxYQJSqPB0m1yCX6qhAET1Rd1Tx3snpKdA9IG1MT2s8uvFnAuKu00RbiVJhro"
            client-reference-id={currentUser?.uid}
          />
        </div>
      </div>
    </div>
  );
}

export default Plans;
